/**
 * Track Header Component (mixing controls)
 */

import React from "react";
import { Track, DraftTrack } from "@jamroom/shared";
import { useMixerStore } from "../store/mixerStore";
import { useSessionStore } from "../store/sessionStore";
import { apiClient } from "../api/client";
import JamroomIconButton from "./JamroomIconButton";
import "./TrackHeader.css";

interface TrackHeaderProps {
  track?: Track;
  draft?: DraftTrack;
  isDraft: boolean;
}

const TrackHeader: React.FC<TrackHeaderProps> = ({ track, draft, isDraft }) => {
  const id = track?.id || draft?.localId || "";
  const name = track?.displayName || draft?.displayName || "Unknown";
  const author = track?.authorName || draft?.authorName || "";

  const { trackParams, setTrackMute, setTrackSolo, setTrackGain, removeTrack: removeMixerTrack } =
    useMixerStore();
  const { removeTrack: removeSessionTrack, removeDraft, permissions } = useSessionStore();
  const params = trackParams.get(id) || { mute: false, solo: false, gain: 1, nudgeMs: 0 };
  const canDelete = isDraft || Boolean(permissions.isOwner) || track?.kind === "recording";
  const isNonPersistentTrack = Boolean(track) && !track?.objectKey;
  const canDownload = Boolean(draft || (track && track.objectKey));

  const getExtension = () => {
    if (draft) {
      if (draft.format === "mp3") return "mp3";
      return draft.format === "wav" ? "wav" : "webm";
    }

    if (!track) return "wav";
    if (track.format === "wav") return "wav";
    if (track.format === "mp3") return "mp3";
    return "webm";
  };

  const sanitizeFileName = (value: string) => {
    const cleaned = value.trim().replace(/[<>:\"/\\\\|?*]+/g, "_");
    return cleaned || "track";
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    if (!id) return;

    if (!window.confirm(`Delete "${name}"?`)) {
      return;
    }

    try {
      if (isDraft || !track) {
        removeDraft(id);
        removeMixerTrack(id);
        return;
      }

      await apiClient.deleteTrack(track.sessionId, track.id);
      removeSessionTrack(track.id);
      removeMixerTrack(track.id);
    } catch (error) {
      console.error("Failed to delete track:", error);
      alert("Failed to delete track");
    }
  };

  const handleDownload = async () => {
    if (!canDownload) return;

    try {
      let blob: Blob;
      if (draft) {
        blob = draft.blob;
      } else if (track) {
        blob = await apiClient.fetchTrackAudio(track.sessionId, track.id);
      } else {
        return;
      }

      const extension = getExtension();
      const filename = `${sanitizeFileName(name)}.${extension}`;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Failed to download track:", error);
      alert("Failed to download track");
    }
  };

  return (
    <div className={`track-header ${isDraft ? "draft" : ""}`}>
      <div className="track-info">
        <div className="track-name">{name}</div>
        {isDraft && <div className="draft-badge">DRAFT</div>}
        {isNonPersistentTrack && <div className="draft-badge smoke-badge">NON PERSISTENT</div>}
        <div className="track-author">{author}</div>
      </div>

      <div className="track-controls">
        <JamroomIconButton
          iconId={params.mute ? "jr-mute" : "jr-volume"}
          ariaLabel={params.mute ? "Unmute track" : "Mute track"}
          pressed={params.mute}
          size={32}
          title={params.mute ? "Unmute" : "Mute"}
          onClick={() => setTrackMute(id, !params.mute)}
          className="mute-btn"
        />
        <button
          className={`jr-icon-btn solo-btn ${params.solo ? "jr-icon-btn--active" : ""}`}
          onClick={() => setTrackSolo(id, !params.solo)}
          title="Solo"
        >
          S
        </button>
        {canDelete && (
          <button className="jr-icon-btn jr-icon-btn--danger delete-btn" onClick={handleDelete} title="Delete track">
            X
          </button>
        )}
        {canDownload && (
          <button className="jr-icon-btn download-btn" onClick={handleDownload} title="Download track">
            DL
          </button>
        )}
      </div>

      <div className="track-gain">
        <input
          type="range"
          min="0"
          max="2"
          step="0.01"
          value={params.gain}
          onChange={(e) => setTrackGain(id, Number(e.target.value))}
          className="gain-slider"
          title="Volume"
        />
        <span className="gain-value">{(params.gain * 100).toFixed(0)}%</span>
      </div>
    </div>
  );
};

export default TrackHeader;
